import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './apiClient';

const feeStructureKeys = {
  all: ['feeStructures'],
  lists: () => [...feeStructureKeys.all, 'list'],
  details: () => [...feeStructureKeys.all, 'detail'],
  detail: (id) => [...feeStructureKeys.details(), id],
};

export const useFeeStructures = () => {
  return useQuery({
    queryKey: feeStructureKeys.lists(),
    queryFn: async () => {
      const response = await apiClient.get('fee_structures');
      return response.data;
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
};

export const useFeeStructure = (id, options = {}) => {
  return useQuery({
    queryKey: feeStructureKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.get(`fee_structures/${id}`);
      return response.data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    ...options
  });
};

export const useCreateFeeStructure = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (feeStructureData) => {
      const response = await apiClient.post('fee_structures', feeStructureData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: feeStructureKeys.lists() });
    },
    onError: (error) => {
      console.error('Error creating fee structure:', error);
    }
  });
};

export const useUpdateFeeStructure = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await apiClient.put(`fee_structures/${id}`, data);
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(feeStructureKeys.detail(variables.id), data);
      queryClient.invalidateQueries({ queryKey: feeStructureKeys.lists() });
    },
    onError: (error) => {
      console.error('Error updating fee structure:', error);
    }
  });
};

export const useDeleteFeeStructure = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const response = await apiClient.delete(`fee_structures/${id}`);
      return response.data;
    },
    onSuccess: (data, deletedId) => {
      queryClient.invalidateQueries({ queryKey: feeStructureKeys.lists() });
      queryClient.removeQueries({ queryKey: feeStructureKeys.detail(deletedId) });
    },
    onError: (error) => {
      console.error('Error deleting fee structure:', error);
    }
  });
};